import slugifyWithCounter from '@sindresorhus/slugify';
import Vue from 'vue';

import PublicArticleSearch from './components/PublicArticleSearch.vue';
import TableOfContents from './components/TableOfContents.vue';
import SubcategoriesBar from './components/SubcategoriesBar/SubcategoriesBar.vue';
import { initializeTheme } from './portalThemeHelper.js';
import { directive as onClickaway } from 'vue-clickaway';
import { getHcBlock } from 'shared/components/HelpCenterBlocks/registry';
import {
  HC_BLOCK_ATTR,
  HC_CONFIG_ATTR,
  decodeBlockConfig,
} from 'shared/components/HelpCenterBlocks/blockConfig';

export const getHeadingsfromTheArticle = () => {
  const rows = [];
  const articleElement = document.getElementById('cw-article-content');
  articleElement.querySelectorAll('h1, h2, h3').forEach(element => {
    const slug = slugifyWithCounter(element.innerText);
    element.id = slug;
    element.className = 'scroll-mt-24 heading';
    element.innerHTML += `<a class="permalink text-slate-600 ml-3" href="#${slug}" title="${element.innerText}" data-turbolinks="false">#</a>`;
    rows.push({
      slug,
      title: element.innerText,
      tag: element.tagName.toLowerCase(),
    });
  });
  return rows;
};

export const openExternalLinksInNewTab = () => {
  const { customDomain, hostURL } = window.portalConfig;
  const isSameHost =
    window.location.href.includes(customDomain) ||
    window.location.href.includes(hostURL);

  // Modify external links only on articles page
  const isOnArticlePage =
    isSameHost && document.querySelector('#cw-article-content') !== null;

  document.addEventListener('click', event => {
    if (!isOnArticlePage) return;

    // Some of the links come wrapped in strong tag through prosemirror

    const isTagAnchor = event.target.tagName === 'A';
    const isParentTagAnchor =
      event.target.tagName === 'STRONG' &&
      event.target.parentNode.tagName === 'A';

    if (isTagAnchor || isParentTagAnchor) {
      const link = isTagAnchor ? event.target : event.target.parentNode;

      const isInternalLink =
        link.hostname === window.location.hostname ||
        link.href.includes(customDomain) ||
        link.href.includes(hostURL);

      if (!isInternalLink) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer'; // Security and performance benefits
        // Prevent default if you want to stop the link from opening in the current tab
        event.stopPropagation();
      }
    }
  });
};

export const InitializationHelpers = {
  navigateToLocalePage: () => {
    const allLocaleSwitcher = document.querySelector('.locale-switcher');

    if (!allLocaleSwitcher) {
      return false;
    }

    const { portalSlug } = allLocaleSwitcher.dataset;
    allLocaleSwitcher.addEventListener('change', event => {
      window.location = `/hc/${portalSlug}/${event.target.value}/`;
    });
    return false;
  },

  initializeSearch: () => {
    const isSearchContainerAvailable = document.querySelector('#search-wrap');
    if (isSearchContainerAvailable) {
      new Vue({
        components: { PublicArticleSearch },
        directives: {
          'on-clickaway': onClickaway,
        },
        template: '<PublicArticleSearch />',
      }).$mount('#search-wrap');
    }
  },

  initializeTableOfContents: () => {
    const isOnArticlePage = document.querySelector('#cw-hc-toc');
    if (isOnArticlePage) {
      new Vue({
        components: { TableOfContents },
        data: { rows: getHeadingsfromTheArticle() },
        template: '<table-of-contents :rows="rows" />',
      }).$mount('#cw-hc-toc');
    }
  },

  initializeSubcategoriesBar: () => {
    const container = document.querySelector('#subcategories-bar-wrap');
    if (!container) {
      return;
    }

    let widgetData;
    try {
      widgetData = JSON.parse(container.dataset.json);
    } catch (error) {
      return;
    }

    const { subcategories, selectedSubcategory, basePath } = widgetData;
    const { categoriesTranslations = {} } = window.portalConfig;

    new Vue({
      components: { SubcategoriesBar },
      data: {
        subcategories,
        selectedSubcategory,
        basePath,
        translations: categoriesTranslations,
      },
      template:
        '<subcategories-bar :subcategories="subcategories" :selected-subcategory="selectedSubcategory" :base-path="basePath" :translations="translations" />',
    }).$mount('#subcategories-bar-wrap');
  },

  appendPlainParamToURLs: () => {
    document.getElementsByTagName('a').forEach(aTagElement => {
      if (aTagElement.href && aTagElement.href.includes('/hc/')) {
        const url = new URL(aTagElement.href);
        url.searchParams.set('show_plain_layout', 'true');

        aTagElement.setAttribute('href', url);
      }
    });
  },

  initializeThemesInPortal: initializeTheme,

  // Hydrate Help Center blocks wherever they appear — inside article bodies
  // (Article Templates) and on the portal home (block-based custom sections).
  // Each inert placeholder (<div data-hc-block data-hc-config>) is replaced by
  // its shared Vue renderer, reading DATA-only config. Unknown types and
  // already-mounted nodes are skipped, so re-runs (turbolinks) never double-mount.
  initializeHelpCenterBlocks: () => {
    const placeholders = document.querySelectorAll(`[${HC_BLOCK_ATTR}]`);
    if (!placeholders.length) return;

    placeholders.forEach(node => {
      if (node.dataset.hcMounted === 'true') return;

      const block = getHcBlock(node.getAttribute(HC_BLOCK_ATTR));
      if (!block) return;

      const config = decodeBlockConfig(node.getAttribute(HC_CONFIG_ATTR));
      node.innerHTML = '';
      node.dataset.hcMounted = 'true';

      const mountPoint = document.createElement('div');
      node.appendChild(mountPoint);
      new Vue({
        render: h => h(block.Renderer, { props: { config } }),
      }).$mount(mountPoint);
    });
  },

  initialize: () => {
    openExternalLinksInNewTab();
    InitializationHelpers.initializeSubcategoriesBar();
    InitializationHelpers.initializeHelpCenterBlocks();
    if (window.portalConfig.isPlainLayoutEnabled === 'true') {
      InitializationHelpers.appendPlainParamToURLs();
    } else {
      InitializationHelpers.initializeThemesInPortal();
      InitializationHelpers.navigateToLocalePage();
      InitializationHelpers.initializeSearch();
      InitializationHelpers.initializeTableOfContents();
    }
  },

  onLoad: () => {
    InitializationHelpers.initialize();
    if (window.location.hash) {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }

      const a = document.createElement('a');
      a.href = window.location.hash;
      a['data-turbolinks'] = false;
      a.click();
    }
  },
};
